import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdminShopCatalog, ShopProduct, createShopProduct, suggestShopProductCode, updateShopProduct } from "../../../api/shop";
import { useAppDispatch } from "../../../store/store";
import { setGeneralAlertMessage } from "../../../store/slices/generalSlice";
import { Alert, Button, Dialog, EmptyState, Loader, useDialogs } from "../../../ui";
import {
  ErrorTarget,
  ProductForm,
  PhotoOffer,
  buildSaveRequest,
  editorView,
  emptyForm,
  formCombinations,
  formFromProduct,
  isFormDirty,
  isFormProblem,
  leafCategoryId,
  missingPhotoKeys,
  rebuildSkus,
  refreshStock,
  removedSkus,
  scrollTargetId,
  setStockForAll,
  targetId,
  unlinkedPhotos,
  updateSku,
  updateStylePhotos,
} from "./editorModel";
import type { Combinations } from "./combinations";
import { isStockChangedError, locateSaveError } from "./serverErrors";
import { BasicsSection } from "./sections/BasicsSection";
import { TypeSection } from "./sections/TypeSection";
import { VariantsSection } from "./sections/VariantsSection";
import { PhotosSection } from "./sections/PhotosSection";
import { CombinationsTable } from "./sections/CombinationsTable";
import "../styles.scss";
import "../shared/styles.scss";
import "./styles.scss";

/** The catalog the editor builds from: until it has loaded, the editor shows a loader (or the error and a retry). */
export type CatalogStatus = { state: "loading" } | { state: "ready" } | { state: "failed"; message: string };

export type ProductEditorProps = {
  open: boolean;
  /** The product being edited; null adds a new one */
  product: ShopProduct | null;
  catalog: AdminShopCatalog;
  catalogStatus: CatalogStatus;
  onRetryCatalog: () => void;
  onClose: () => void;
  onSaved: () => void;
  /** The server refused the save (stock may have moved under the open form): the products read again, or null */
  onSaveFailed: () => Promise<ShopProduct[] | null>;
};

type SaveError = { message: string; target: ErrorTarget | null };

/** The wide drawer that adds or edits a product. Mount it with a new key per open: its state starts from `product`. */
export function ProductEditor({
  open,
  product,
  catalog,
  catalogStatus,
  onRetryCatalog,
  onClose,
  onSaved,
  onSaveFailed,
}: ProductEditorProps) {
  const dispatch = useAppDispatch();
  const { confirm } = useDialogs();
  const [form, setForm] = useState<ProductForm>(() => (product ? formFromProduct(product) : emptyForm()));
  // Where the form started, to ask before closing with unsaved edits
  const [start, setStart] = useState<ProductForm>(form);
  // The product as last read from the server (read again after a stale-stock refusal)
  const [saved, setSaved] = useState<ShopProduct | null>(product);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [suggesting, setSuggesting] = useState(false);
  const [error, setError] = useState<SaveError | null>(null);
  const suggestSeq = useRef(0);
  const confirmingClose = useRef(false);
  const ready = catalogStatus.state === "ready";

  // Every edit goes through here and clears a shown save error (the admin is fixing it)
  const change = useCallback((update: (prev: ProductForm) => ProductForm) => {
    setError(null);
    setForm(update);
  }, []);
  const patch = useCallback((values: Partial<ProductForm>) => change((prev) => ({ ...prev, ...values })), [change]);
  // Row and photo edits need the combinations of the form they apply to (a carried row edits the old SKU)
  const editWithCombos = (update: (prev: ProductForm, prevCombos: Combinations) => ProductForm) =>
    change((prev) => update(prev, formCombinations(prev, catalog)));

  const { kind, optionValueIds, sizeIds } = form;
  const combos = useMemo(() => formCombinations({ kind, optionValueIds, sizeIds }, catalog), [kind, optionValueIds, sizeIds, catalog]);
  // What the editor shows and saves: the old style's SKUs and photos carried to the first new style
  const view = useMemo(() => editorView(form, combos), [form, combos]);
  const removal = useMemo(() => removedSkus(saved, combos), [saved, combos]);
  const missing = useMemo(() => missingPhotoKeys(form, combos), [form, combos]);
  const offers = useMemo(() => unlinkedPhotos(form, combos), [form, combos]);

  // A new product's code follows the category and sports until the admin types one
  const leaf = leafCategoryId(form);
  const sportKey = form.sportIds.join(",");
  useEffect(() => {
    if (form.codeTouched || !leaf) return;
    const seq = ++suggestSeq.current;
    setSuggesting(true);
    const timer = window.setTimeout(async () => {
      try {
        const res = await suggestShopProductCode(leaf, sportKey ? sportKey.split(",") : []);
        if (seq !== suggestSeq.current) return;
        const code = typeof res?.data === "string" ? res.data : "";
        // A code the admin typed meanwhile wins
        setForm((prev) => (prev.codeTouched ? prev : { ...prev, code }));
      } catch (err) {
        // No suggestion: the server generates the code on save
        console.error("Error suggesting a product code", err);
      } finally {
        if (seq === suggestSeq.current) setSuggesting(false);
      }
    }, 250);
    return () => {
      window.clearTimeout(timer);
      setSuggesting(false);
    };
  }, [form.codeTouched, leaf, sportKey]);

  const errorAt = (id: string) => (error?.target && targetId(error.target) === id ? error.message : null);

  // Show the problem in the alert and on the row/section it names, scrolled into view
  const report = (message: string, target: ErrorTarget | null) => {
    setError({ message, target });
    dispatch(setGeneralAlertMessage({ type: "error", message }));
    if (!target) return;
    const id = scrollTargetId(target);
    window.requestAnimationFrame(() =>
      Array.from(document.querySelectorAll<HTMLElement>("[data-target]"))
        .find((el) => el.dataset.target === id)
        ?.scrollIntoView({ block: "center", behavior: "smooth" })
    );
  };

  const handleSave = async () => {
    if (!ready) return;
    const request = buildSaveRequest(form, catalog, combos);
    if (isFormProblem(request)) {
      report(request.message, request.target);
      return;
    }
    if (removal.count > 0) {
      const ok = await confirm({
        title: "Delete SKUs",
        tone: "danger",
        confirmLabel: "Save and delete",
        cancelLabel: "Back",
        message: (
          <>
            Saving deletes{" "}
            <strong>
              {removal.count} {removal.count === 1 ? "SKU" : "SKUs"}
            </strong>
            {removal.stock > 0 ? (
              <>
                {" "}
                holding <strong>{removal.stock} in stock</strong>
              </>
            ) : null}
            : {removal.labels.join(", ")}. Past orders keep their details.
          </>
        ),
      });
      if (!ok) return;
    }
    setSaving(true);
    setError(null);
    try {
      if (product) await updateShopProduct(product.id, request);
      else await createShopProduct(request);
      dispatch(setGeneralAlertMessage({ type: "success", message: product ? "Product updated successfully" : "Product added successfully" }));
      onSaved();
    } catch (err: any) {
      const message = err?.message || "Failed to save product";
      report(message, locateSaveError(message, form, combos));
      const products = await onSaveFailed();
      // Orders moved the stock: expect the fresh stock (and show it where the admin didn't change it), keep the rest
      const fresh = product && isStockChangedError(message) ? products?.find((p) => p.id === product.id) : undefined;
      if (fresh) {
        setSaved(fresh);
        setForm((prev) => refreshStock(prev, fresh));
        setStart((prev) => refreshStock(prev, fresh));
      }
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || uploading > 0;
  // Escape, the ✕ and Cancel: unsaved edits ask first
  const close = async () => {
    if (busy || confirmingClose.current) return;
    if (ready && isFormDirty(start, form, catalog)) {
      confirmingClose.current = true;
      const ok = await confirm({
        title: "Discard changes",
        tone: "danger",
        confirmLabel: "Discard",
        cancelLabel: "Keep editing",
        message: product ? (
          <>
            Your changes to "<strong>{product.name}</strong>" aren't saved. Close without saving them?
          </>
        ) : (
          "This product isn't saved yet. Close without saving it?"
        ),
      });
      confirmingClose.current = false;
      if (!ok) return;
    }
    onClose();
  };

  const adoptOffer = (offer: PhotoOffer) =>
    editWithCombos((prev, prevCombos) =>
      updateStylePhotos(prev, prevCombos, offer.toKey, (list) => [...list, ...offer.urls.filter((url) => !list.includes(url))])
    );

  return (
    <Dialog
      open={open}
      onClose={close}
      title={product ? "Edit Product" : "Add Product"}
      subtitle={product?.code || undefined}
      size="xl"
      closeOnBackdrop={false}
      busy={busy}
      bodyClassName="shop-form product-editor"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={saving}
            disabled={!ready || uploading > 0 || combos.tooMany || !form.name.trim() || !(Number(form.price) > 0)}
          >
            {product ? "Update" : "Add"}
          </Button>
        </>
      }
    >
      {catalogStatus.state === "loading" ? (
        <Loader label="Loading the catalog…" />
      ) : catalogStatus.state === "failed" ? (
        <EmptyState
          title="The catalog couldn't be loaded."
          description={catalogStatus.message}
          action={<Button onClick={onRetryCatalog}>Try again</Button>}
        />
      ) : (
        <>
          <BasicsSection form={form} catalog={catalog} onChange={patch} suggesting={suggesting} errorAt={errorAt} />
          <TypeSection kind={form.kind} onChange={(next) => patch({ kind: next })} />
          {removal.count > 0 && (
            <Alert tone="warn" className="editor-removal">
              Saving deletes {removal.count} {removal.count === 1 ? "SKU" : "SKUs"}
              {removal.stock > 0 ? ` holding ${removal.stock} in stock` : ""}: {removal.labels.join(", ")}. Past orders
              keep their details.
            </Alert>
          )}
          {form.kind === "variants" && (
            <VariantsSection form={form} catalog={catalog} combos={combos} onChange={patch} errorAt={errorAt} />
          )}
          <PhotosSection
            combos={combos}
            images={view.styleImages}
            missing={missing}
            offers={offers}
            errorAt={errorAt}
            onChange={(key, update) => editWithCombos((prev, prevCombos) => updateStylePhotos(prev, prevCombos, key, update))}
            onUseOffer={adoptOffer}
            onBusyChange={(delta) => setUploading((n) => n + delta)}
          />
          <CombinationsTable
            form={view}
            combos={combos}
            errorAt={errorAt}
            onUpdateSku={(key, values) => editWithCombos((prev, prevCombos) => updateSku(prev, key, values, prevCombos))}
            onSetStockForAll={(stock) => editWithCombos((prev, prevCombos) => setStockForAll(prev, prevCombos, stock))}
            onRebuild={() => editWithCombos((prev, prevCombos) => rebuildSkus(prev, prevCombos))}
          />
        </>
      )}
    </Dialog>
  );
}

export default ProductEditor;
